import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

function richTextRequired(control: AbstractControl): { [key: string]: any } | null {
  const value = control.value || '';
  const plainText = value.replace(/<[^>]*>/g, '').trim();
  return plainText ? null : { required: true };
}
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { StepType } from '@progress/kendo-angular-layout';
import { IApiAgingInvoiceEmailTemplate, IApiAgingNoticeInput, IApiSageInvoice } from 'src/app/shared/modules/graphql/types/types';
import { LoaderService } from 'src/app/shared/modules/loader/loader.service';
import { NotificationsService } from 'src/app/shared/modules/notifications/notifications.service';
import { InvoiceService, SageInvoiceAgingReminderService } from 'src/app/shared/services';

@Component({
  selector: 'app-sage-invoice-reminder-modal',
  templateUrl: './sage-invoice-reminder-modal.component.html',
  styleUrls: ['./sage-invoice-reminder-modal.component.scss'],
})
export class SageInvoiceReminderModalComponent extends DialogContentBase implements OnInit {
  @Input() invoices: IApiSageInvoice[] = [];

  public fromEmailOptions: string[] = [
    'accounting@nefco.net',
    'tdoucette@nefco.net',
    'dkeelty@nefco.net',
    'vabell@nefco.net',
    'csmith@nefco.net',
    'aganley@nefco.net',
    'odavis@nefco.net',
  ];

  public get steps() {
    return [
      { label: "Contact Emails", isValid: this.isStep1Valid },
      { label: "Email Templates", isValid: this.isStep2Valid },
    ];
  }

  public stepType: StepType = "full";
  public current = 0;
  public emailTemplates: IApiAgingInvoiceEmailTemplate[] = [];

  public form: FormGroup;

  constructor(
    public dialog: DialogRef,
    private loader: LoaderService,
    private sageInvoiceAgingReminderService: SageInvoiceAgingReminderService,
    private fb: FormBuilder,
    private notificationService: NotificationsService
  ) {
    super(dialog);
  }

  ngOnInit() {
    this.initForm();
    console.log(this.invoices);
    this.loader.show$(this.sageInvoiceAgingReminderService.getAgingEmailTemplates()).subscribe((res) => {
      this.emailTemplates = res;
    });
  }

  private initForm() {
    this.form = this.fb.group({
      invoices: this.fb.array(
        this.invoices.map((inv) =>
          this.fb.group({
            invoiceNumber: [inv.invoiceNumber],
            customerEmail: [inv.customerEmail ? inv.customerEmail.split(',').map(e => e.trim()) : [], [Validators.required]],
          })
        )
      ),
      template: [null],
      from: ['accounting@nefco.net', Validators.required],
      subject: ['', Validators.required],
      body: ['', richTextRequired],
    });
  }

  get invoiceFormArray(): FormArray {
    return this.form.get('invoices') as FormArray;
  }

  public valueNormalizer = (text$: Observable<string>): Observable<string | null> => {
    return text$.pipe(
      map((text: string) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (emailRegex.test(text)) {
          return text;
        }
        return null;
      })
    );
  };

  public onTemplateChange(template: IApiAgingInvoiceEmailTemplate): void {
    if (!template?.title) {
      this.form.patchValue({
        subject: "",
        body: "",
      });
    } else {
      this.form.patchValue({
        subject: template.subject,
        body: template.body,
      });
    }
  }

  close(result = false) {
    this.dialog.close(result);
  }

  public prev(): void {
    if (this.current > 0) {
      this.current -= 1;
    }
  }

  public next(): void {
    if (this.current === 0) {
      if (this.invoiceFormArray.valid) {
        this.current += 1;
      } else {
        this.invoiceFormArray.markAllAsTouched();
      }
    } else if (this.current < this.steps.length - 1) {
      this.current += 1;
    }
  }

  public submit(): void {
    if (this.form.valid) {
      const formValue = this.form.value;
      const result: IApiAgingNoticeInput = {
        title: formValue.template.title,
        subject: formValue.subject,
        body: formValue.body,
        from: formValue.from,
        invoices: formValue.invoices.map((inv: any) => {
          const originalInvoice = this.invoices.find((i) => i.invoiceNumber === inv.invoiceNumber);
          return {
            customerId: originalInvoice?.customerId,
            customerName: originalInvoice?.customerName,
            dueDate: originalInvoice?.dueDate,
            invoiceDate: originalInvoice?.invoiceDate,
            invoiceNumber: inv.invoiceNumber,
            referenceNumber: originalInvoice?.referenceNumber,
            totalTxnAmountDue: originalInvoice?.totalTxnAmountDue,
            email: inv?.customerEmail?.join(','),
            sageInvoiceId: originalInvoice?.id,
            documentId: originalInvoice?.documentId,
          };
        }),
      };
      this.loader.show$(this.sageInvoiceAgingReminderService.sendAgingInvoiceNotice(result).pipe(this.notificationService.snackbarErrorPipe())).subscribe((res) => {
        if (res.success) {
          this.notificationService.alert(res.message || "Aging invoice notice sent successfully", "Success", "Ok", null, "fa-solid fa-circle-check fa-xl success");
          this.close(true);
        } else {
          this.notificationService.alert(res.message || "Something went wrong", "Error");
        }
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  public get isStep1Valid(): boolean {
    return this.invoiceFormArray.valid;
  }

  public get isStep2Valid(): boolean {
    return (
      !!this.form.get('template')?.valid &&
      !!this.form.get('from')?.valid &&
      !!this.form.get('subject')?.valid &&
      !!this.form.get('body')?.valid
    );
  }
}
